// src/services/api.js
import axios from 'axios';

// API calling Render
const AUTH_URL = 'https://digis-backend.onrender.com/auth'; // Authentication base URL
const API_URL = 'https://digis-backend.onrender.com/api'; // API base URL for actions

// Set up Axios instance
const api = axios.create({
    baseURL: API_URL,
});

// Add a request interceptor
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('authToken'); // Retrieve the token from local storage
    if (token) {
        config.headers.Authorization = `Token ${token}`; // Assuming your backend uses 'Token' scheme
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Function to register a new user
export const registerUser = async (userData) => {
    try {
        const response = await axios.post(`${AUTH_URL}/registration/`, userData);
        return response.data; // Returns the user data from the response
    } catch (error) {
        throw error.response.data; // Customize this based on your needs
    }
};

// Function to log in a user
export const loginUser = async (credentials) => {
    try {
        const response = await axios.post(`${AUTH_URL}/login/`, credentials);
        console.log('Login Response:', response.data); // Log the response data
        // Store user ID in local storage
        localStorage.setItem('user', response.data.user); // Use 'userId' for consistency
        console.log('User ID saved:', response.data.user);
        return { token: response.data.key }; // Return the token
    } catch (error) {
        throw error.response.data;
    }
};

// Function to fetch user profile in side navigation menu
export const fetchUserProfile = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Returns the user profile data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to fetch tasks
export const fetchTasks = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/tasks/`, {
            headers: { Authorization: `Token ${token}` } // Adjust based on your authentication method
        });
        return response.data; // Returns the list of birthdays
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};

// Function to create a new task
export const createTask = async (taskData, token) => {
    try {
        const response = await axios.post(`${API_URL}/tasks/`, taskData, {
            headers: { Authorization: `Token ${token}` } // Adjust based on your authentication method
        });
        return response.data; // Returns the created birthday data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};

// Function to update a task
export const updateTask = async (taskId, taskData, token) => {
    try {
        const response = await axios.put(`${API_URL}/tasks/${taskId}/`, taskData, {
            headers: { Authorization: `Token ${token}` } // Adjust based on your authentication method
        });
        return response.data; // Returns the updated birthday data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};

// Function to delete a task
export const deleteTask = async (taskId, token) => {
    try {
        const response = await axios.delete(`${API_URL}/tasks/${taskId}/`, {
            headers: { Authorization: `Token ${token}` } // Adjust based on your authentication method
        });
        return response.data; // Optionally return a success message or response
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to fetch the user's profile
export const fetchProfile = async (token) => {
    try {
        const response = await axios.get(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Returns the profile data
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};


// Function to update the user's profile
export const updateProfile = async (profileData, token) => {
    try {
        const response = await axios.patch(`${API_URL}/profile/`, profileData, {
            headers: {
                Authorization: `Token ${token}`
                // Do NOT add Content-Type when using FormData; axios sets it automatically.
            }
        });
        return response.data; // Returns the updated profile data
    } catch (error) {
        console.error('API error:', error.response?.data || error.message);
        throw error.response?.data || error;
    }
};



// Function to delete the user's profile
export const deleteProfile = async (token) => {
    try {
        const response = await axios.delete(`${API_URL}/profile/`, {
            headers: { Authorization: `Token ${token}` }
        });
        return response.data; // Optionally return a success message or confirmation
    } catch (error) {
        throw error.response.data; // Handle the error response
    }
};



// Get all candidates
export const fetchCandidates = async () => {
    try {
        const response = await api.get('/candidates/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Create a new candidate
export const createCandidate = async (candidateData) => {
    try {
        const response = await api.post('/candidates/', candidateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update an existing candidate
export const updateCandidate = async (candidateId, candidateData) => {
    try {
        const response = await api.put(`/candidates/${candidateId}/`, candidateData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete a candidate
export const deleteCandidate = async (candidateId) => {
    try {
        const response = await api.delete(`/candidates/${candidateId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};


// Outstaff API functions 
// Get all job briefs
export const fetchJobBriefs = async () => {
    try {
        const response = await api.get('/job-briefs/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Create a new job brief
export const createJobBrief = async (jobBriefData) => {
    try {
        const response = await api.post('/job-briefs/', jobBriefData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update an existing job brief
export const updateJobBrief = async (jobBriefId, jobBriefData) => {
    try {
        const response = await api.put(`/job-briefs/${jobBriefId}/`, jobBriefData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete a job brief
export const deleteJobBrief = async (jobBriefId) => {
    try {
        const response = await api.delete(`/job-briefs/${jobBriefId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};



// ======================
// OUTSOURCE REQUESTS
// ======================
// outsourceRequestsApi.js
// Get all outsource requests
export const fetchOutsourceRequests = async () => {
    try {
        const response = await api.get('/requests/');
        return Array.isArray(response.data) ? response.data : response.data.results;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Get a single outsource request by ID
export const fetchOutsourceRequestById = async (requestId) => {
    try {
        const response = await api.get(`/requests/${requestId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Create a new outsource request
export const createOutsourceRequest = async (requestData) => {
    try {
        const response = await api.post('/requests/', requestData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update an existing outsource request
export const updateOutsourceRequest = async (requestId, requestData) => {
    try {
        const response = await api.put(`/requests/${requestId}/`, requestData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete an outsource request
export const deleteOutsourceRequest = async (requestId) => {
    try {
        const response = await api.delete(`/requests/${requestId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};



// ======================
// OUTSOURCE STAGES
// ======================

// Get all stages
export const fetchOutsourceStages = async () => {
    try {
        const response = await api.get('/stages/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Create a new stage
export const createOutsourceStage = async (stageData) => {
    try {
        const response = await api.post('/stages/', stageData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update an existing stage
export const updateOutsourceStage = async (stageId, stageData) => {
    try {
        const response = await api.put(`/stages/${stageId}/`, stageData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete a stage
export const deleteOutsourceStage = async (stageId) => {
    try {
        const response = await api.delete(`/stages/${stageId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};



// ======================
// OUTSOURCE PROGRESS
// ======================

// Get all progress entries (optionally filter by outsource_request in frontend)
export const fetchOutsourceProgress = async () => {
    try {
        const response = await api.get('/progress/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Create progress for a request
export const createOutsourceProgress = async (progressData) => {
    try {
        const response = await api.post('/progress/', progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Update an existing progress entry
export const updateOutsourceProgress = async (progressId, progressData) => {
    try {
        const response = await api.patch(`/progress/${progressId}/`, progressData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};

// Delete a progress entry
export const deleteOutsourceProgress = async (progressId) => {
    try {
        const response = await api.delete(`/progress/${progressId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error;
    }
};
